// The owner's weekly review (step 4.10): the research card a draft carries (outbox.metadata.research, the shared
// contract in docs/specs/FIND_REVIEW_RESULTS.md, with the evidence of a reason-led draft, 2.43), grouping drafts by
// angle and confidence, when "Approve all like this" is offered, the one-tap rejection reasons, and the keyboard
// shortcuts. Pure functions: the screen is screens/approvals/OutboxBatch.tsx.
import type { RejectReason, ReviewDraft } from "./types";
import { humanise, webUrl } from "./format";

export type Confidence = "high" | "medium" | "low";

export interface ResearchSource {
  label: string;
  url: string | null;
  fact_id?: string | null;
}
export interface ResearchCheck {
  rule: string;
  ok: boolean;
  detail?: string | null;
}
/** 2.43 Find's reason a reason-led message cites, with the brief lines it used (3.20), each with where it came from. */
export interface ResearchEvidence {
  reason: { label: string | null; line: string; source: string | null; url: string | null };
  brief: { field: string; text: string; url: string | null }[];
}
/** What the research agent found, ready to show beside the draft. */
export interface ResearchCard {
  who: string | null;
  firm: string | null;
  why_now: string | null;
  hook: string | null;
  sources: ResearchSource[];
  confidence: Confidence | null;
  checks: ResearchCheck[];
  evidence: ResearchEvidence | null;
  /** True when the draft had no research card and this was built from the older hook fields. */
  legacy: boolean;
}

const text = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v.trim() : null);

/** The research card for a draft, or one built from the hook and its source for drafts written before the contract. */
export function researchOf(d: ReviewDraft): ResearchCard | null {
  const r = d.research && typeof d.research === "object" && !Array.isArray(d.research) ? (d.research as Record<string, unknown>) : null;
  if (r) {
    const conf = r.confidence === "high" || r.confidence === "medium" || r.confidence === "low" ? r.confidence : null;
    const sources = Array.isArray(r.sources)
      ? r.sources.flatMap((s): ResearchSource[] => {
          if (!s || typeof s !== "object") return [];
          const o = s as Record<string, unknown>;
          const url = webUrl(text(o.url));
          const label = text(o.label) ?? (url ? new URL(url).hostname.replace(/^www\./, "") : null);
          return label ? [{ label, url, fact_id: text(o.fact_id) }] : [];
        })
      : [];
    const checks = Array.isArray(r.checks)
      ? r.checks.flatMap((c): ResearchCheck[] => {
          if (!c || typeof c !== "object") return [];
          const o = c as Record<string, unknown>;
          const rule = text(o.rule);
          return rule ? [{ rule, ok: o.ok === true, detail: text(o.detail) }] : [];
        })
      : [];
    return {
      who: text(r.who),
      firm: text(r.firm),
      why_now: text(r.why_now),
      hook: text(r.hook) ?? d.hook ?? null,
      sources,
      confidence: conf ?? d.confidence ?? null,
      checks,
      evidence: evidenceOf(r.evidence),
      legacy: false,
    };
  }
  if (!d.hook) return null;
  const url = webUrl(d.hook_source);
  const sources = d.hook_source ? [{ label: url ? new URL(url).hostname.replace(/^www\./, "") : d.hook_source, url }] : [];
  return { who: null, firm: null, why_now: null, hook: d.hook, sources, confidence: null, checks: [], evidence: null, legacy: true };
}

const record = (v: unknown): Record<string, unknown> | null => (v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null);

/** metadata.research.evidence (engine/drafting.py evidence_card), or null when the draft cites no reason from Find. */
export function evidenceOf(v: unknown): ResearchEvidence | null {
  const e = record(v);
  const r = record(e?.reason);
  const line = text(r?.line);
  if (!e || !r || !line) return null;
  const brief = Array.isArray(e.brief)
    ? e.brief.flatMap((b) => {
        const o = record(b);
        const t = text(o?.text);
        const field = text(o?.field);
        return o && t && field ? [{ field, text: t, url: webUrl(text(o.url)) }] : [];
      })
    : [];
  return { reason: { label: text(r.label), line, source: text(r.source), url: webUrl(text(r.url)) }, brief };
}

/** How the screen names a brief line. */
export const BRIEF_FIELD_LABEL: Record<string, string> = { lead_offer: "What they push", sells_to: "Who they sell to" };

/** The checks that failed: these are shown first, in the danger style. */
export function failedChecks(card: ResearchCard | null): ResearchCheck[] {
  return card?.checks.filter((c) => !c.ok) ?? [];
}

export const CONFIDENCE_LABEL: Record<Confidence | "none", string> = { high: "High confidence", medium: "Medium confidence", low: "Low confidence", none: "Not rated" };

/** The angle and confidence pair "Approve all like this" works on. */
export function groupKey(d: ReviewDraft): string {
  const card = researchOf(d);
  return `${d.angle ?? "none"}|${card?.confidence ?? d.confidence ?? "none"}`;
}

export function groupLabel(d: ReviewDraft): string {
  const card = researchOf(d);
  const angle = d.angle_label ?? (d.angle ? humanise(d.angle) : "No angle");
  return `${angle}, ${CONFIDENCE_LABEL[card?.confidence ?? d.confidence ?? "none"].toLowerCase()}`;
}

export type Decision = { kind: "approved"; edited: boolean } | { kind: "rejected"; reason: RejectReason };

/** At least this many approvals in a group before the rest of it can be approved in one go. */
export const APPROVE_ALL_AFTER = 3;

/**
 * "Approve all like this": the drafts still to decide that share this draft's angle and confidence, offered once the
 * owner has approved at least three of that group by hand and rejected none. A draft whose checks failed, or whose
 * channel is no longer allowed, is never swept up: it waits for its own look. Drafts with no angle are never grouped.
 */
export function approveAllLike(drafts: ReviewDraft[], decisions: Record<string, Decision>, d: ReviewDraft): { eligible: boolean; ids: string[]; approved: number; label: string } {
  const key = groupKey(d);
  const same = drafts.filter((x) => groupKey(x) === key);
  const approved = same.filter((x) => decisions[x.id]?.kind === "approved").length;
  const rejected = same.some((x) => decisions[x.id]?.kind === "rejected");
  const ids = same.filter((x) => !decisions[x.id] && x.channel_allowed !== false && failedChecks(researchOf(x)).length === 0).map((x) => x.id);
  const eligible = Boolean(d.angle) && approved >= APPROVE_ALL_AFTER && !rejected && ids.length > 0;
  return { eligible, ids, approved, label: groupLabel(d) };
}

/** One-tap reasons. The first three are new (migration 20261112110000); legal, tone and too pushy hold back autonomy. */
export const REJECT_REASONS: { value: RejectReason; label: string; key: string }[] = [
  { value: "wrong_person", label: "Wrong person", key: "1" },
  { value: "too_pushy", label: "Too pushy", key: "2" },
  { value: "not_a_fit", label: "Not a fit", key: "3" },
  { value: "facts", label: "Facts wrong", key: "4" },
  { value: "tone", label: "Tone", key: "5" },
  { value: "timing", label: "Timing", key: "6" },
  { value: "legal", label: "Legal", key: "7" },
  { value: "other", label: "Other", key: "8" },
];

export function reasonLabel(r: string): string {
  return REJECT_REASONS.find((x) => x.value === r)?.label ?? humanise(r);
}

/** "23 of 60 reviewed". */
export function progressText(drafts: { id: string }[], decisions: Record<string, unknown>): string {
  const done = drafts.filter((d) => decisions[d.id]).length;
  return `${done} of ${drafts.length} reviewed`;
}

export type ShortcutAction = "approve" | "edit" | "reject" | "next" | "prev" | null;

/** Desktop shortcuts: a approve, e edit, r reject, j next, k previous. Never while typing or with a modifier held. */
export function shortcutFor(e: { key: string; ctrlKey?: boolean; metaKey?: boolean; altKey?: boolean; target?: EventTarget | null }): ShortcutAction {
  if (e.ctrlKey || e.metaKey || e.altKey) return null;
  const t = e.target as HTMLElement | null;
  if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName ?? ""))) return null;
  switch (e.key) {
    case "a":
      return "approve";
    case "e":
      return "edit";
    case "r":
      return "reject";
    case "j":
      return "next";
    case "k":
      return "prev";
    default:
      return null;
  }
}

/** The next draft still to decide after index i (wrapping), or -1 when none is left. */
export function nextOpen(drafts: { id: string }[], decisions: Record<string, unknown>, i: number): number {
  for (let step = 1; step <= drafts.length; step++) {
    const j = (i + step) % drafts.length;
    if (!decisions[drafts[j]!.id]) return j;
  }
  return -1;
}
